import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Query, useMutation } from 'react-apollo';
import Helmet from 'react-helmet';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteForeverOutlinedIcon from '@material-ui/icons/DeleteForeverOutlined';
import Loading from 'components/common/Spin';
import Button from 'components/common/Button';
import {
  GET_CURRICULA,
  CREATE_CURRICULUM,
  UPDATE_CURRICULUM,
  DELETE_CURRICULUM,
} from 'graphql/admin/curriculum';
import useSnack from 'hooks/useSnack';
import { useConfirm } from 'material-ui-confirm';
import CurriculumCopyModal from './components/CurriculumCopyModal';

const Curricula = ({}) => {
  const [updateCurriculum] = useMutation(UPDATE_CURRICULUM);
  const [addCurriculum] = useMutation(CREATE_CURRICULUM);
  const [deleteCurriculum] = useMutation(DELETE_CURRICULUM);
  const [selected, setSelected] = useState();
  const user = useSelector(s => s.user.user);
  const snack = useSnack();
  const confirm = useConfirm();
  const canAdd = user.type === 'admin';

  const onEdit = (item) => (e) => {
    const name = window.prompt('Curriculum name', item.name);
    if (name) {
      return updateCurriculum({
        variables: {
          id: item.id,
          data: {
            name,
          },
        },
      }).then(
        () => snack.success('Curriculum updated.'),
        (e) => snack.error(e.message),
      );
    }
  };

  const onCopy = async (name, data) => {
    await addCurriculum({
      variables: {
        data: {
          name,
          modules: {
            data: data,
          },
        },
      },
      refetchQueries: [{ query: GET_CURRICULA }],
    });
    setSelected();
    snack.success('Curriculum copied');
  };

  const onAdd = () => {
    const name = window.prompt('Curriculum name');
    if (name) {
      return addCurriculum({
        variables: {
          data: {
            name,
          },
        },
        refetchQueries: [
          {
            query: GET_CURRICULA,
          },
        ],
      }).then(
        () => snack.success('Curriculum added.'),
        (e) => snack.error(e.message),
      );
    }
  };

  const onDelete = (item) => (e) => {
    confirm().then(() =>
      deleteCurriculum({
        variables: { id: item.id },
        refetchQueries: [{ query: GET_CURRICULA }],
      }),
    );
  };

  return (
    <Box>
      <Helmet title="Curricula" />
      {canAdd && (
        <Box mb={2} textAlign="right">
          <Button onClick={onAdd} variant="contained" color="primary">
            Add Curriculum
          </Button>
        </Box>
      )}
      <Query query={GET_CURRICULA} fetchPolicy="cache-and-network">
        {({ data, loading }) => {
          if (loading) return <Loading spinning />;

          return (
            <Paper>
              <List subheader={<ListSubheader>Curricula</ListSubheader>}>
                {data.curricula.map((i) => (
                  <ListItem
                    button
                    key={i.id}
                    component={Link}
                    to={`/curriculum/${i.id}`}
                  >
                    <ListItemText primary={i.name} />
                    {canAdd && (
                      <ListItemSecondaryAction>
                        <Tooltip title="Copy curriculum">
                          <IconButton onClick={() => setSelected(i)}>
                            <FileCopyOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit curriculum">
                          <IconButton onClick={onEdit(i)}>
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton edge="end" onClick={onDelete(i)}>
                          <DeleteForeverOutlinedIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
            </Paper>
          );
        }}
      </Query>
      {selected && (
        <CurriculumCopyModal
          curriculum_id={selected.id}
          onClose={() => setSelected()}
          onSave={onCopy}
        />
      )}
    </Box>
  );
};

export default Curricula;
